import { ViewConfig } from '@helo/cms'
import { If, flow, tap } from '@helo/utils'
import { isEqual } from 'lodash'
import { matchRoutes } from 'react-router-dom'
import { StoreApi } from 'zustand'

import { ScreenPage } from '../../../pages/ScreenPage'
import { RouteConfig } from '../../../router/routes'
import { AuthSlice } from '../auth/authSlice'
import { CmsSlice } from '../cms'
import { PersistenceSlice } from '../persistence/persistenceSlice'
import { QuerySlice } from '../queryStore/querySlice'
import { QuerySliceActions } from '../queryStore/querySlice.actions'
import { ViewSlice } from '../view/viewSlice'
import { RouterSlice, RouterState } from './routerSlice'

export type RunPageSetupStore = RouterSlice &
  AuthSlice &
  PersistenceSlice &
  QuerySlice &
  ViewSlice &
  CmsSlice

/**
 * Reads pathname and search params from `window.location` and merges them into
 * the router slice when they differ from what is already in the store.
 */
const synchronizeRouteStateFromBrowser = () =>
  flow(
    flow.inputExtends<RunPageSetupStore>(),
    <T extends RunPageSetupStore>(store: T): T => {
      const pathname = window.location.pathname
      // TODO: may need to use encodeURIcomponent on each value. At least had to with react router.
      const searchParams = Object.fromEntries(
        new URLSearchParams(window.location.search).entries(),
      )

      if (
        pathname === store.router.pathname &&
        isEqual(store.router.searchParams, searchParams)
      ) {
        return store
      }

      return {
        ...store,
        router: {
          ...store.router,
          pathname,
          searchParams,
        },
      } as T
    },
  )

/**
 * Reads dynamic segment params from the active React Router match tree and
 * merges `urlParams` into the router slice when they differ from the store.
 */
const synchronizeRouteStateFromRouter = () =>
  flow(
    flow.inputExtends<RunPageSetupStore>(),
    <T extends RunPageSetupStore>(store: T): T => {
      const routeMatches = store.router.router?.state?.matches ?? []
      const urlParams = (routeMatches[routeMatches.length - 1]?.params ??
        {}) as Record<string, string>

      if (isEqual(store.router.urlParams, urlParams)) {
        return store
      }

      return {
        ...store,
        router: {
          ...store.router,
          urlParams,
        },
      } as T
    },
  )

const resetBrowserDocumentTitleForNavigation = () =>
  tap(<T extends RunPageSetupStore>(store: T) => {
    store.browserDocumentTitle.resetForNavigation()
    return store
  })

const createUniqueViewKey = <T extends RouterSlice>(store: T): string => {
  const navstate = JSON.stringify(
    store.router.router!.state!.location.state ?? '',
  )
  return `${store.router.pathname}-${navstate}`
}

const isSameRoute =
  () =>
  <T extends RunPageSetupStore>(store: T): boolean =>
    createUniqueViewKey(store) === store.router.uniqueViewKey

const updateUniqueViewKey = () =>
  flow(
    flow.inputExtends<RunPageSetupStore>(),
    <T extends RunPageSetupStore>(store: T): T => ({
      ...store,
      router: {
        ...store.router,
        uniqueViewKey: createUniqueViewKey(store),
      },
    }),
  )

const setComponentStatus = (componentStatus: RouterState['componentStatus']) =>
  flow(
    flow.inputExtends<RunPageSetupStore>(),
    <T extends RunPageSetupStore>(store: T): T =>
      ({
        ...store,
        router: {
          ...store.router,
          componentStatus,
        },
      }) as T,
  )

const synchronizeRouteConfigFromRoutes = () =>
  flow(
    flow.inputExtends<RunPageSetupStore>(),
    <T extends RunPageSetupStore>(store: T): T => {
      const matches =
        matchRoutes(store.router.routes, store.router.pathname) ?? []
      const match = matches[matches.length - 1]?.route ?? null

      if (store.router.routeConfig === match) {
        return store
      }

      return {
        ...store,
        router: {
          ...store.router,
          routeConfig: match,
        },
      } as T
    },
  )

const setPageSetupRunId = () =>
  flow(
    flow.inputExtends<RunPageSetupStore>(),
    <T extends RunPageSetupStore>(store: T): T =>
      ({
        ...store,
        router: {
          ...store.router,
          pageSetupRunId: crypto.randomUUID(),
        },
      }) as T,
  )

const isCurrentPageSetupRunID =
  (storeApi: StoreApi<RunPageSetupStore>) => (store: RunPageSetupStore) =>
    storeApi.getState().router.pageSetupRunId === store.router.pageSetupRunId

const throwIfStalePageSetup = (storeApi: StoreApi<RunPageSetupStore>) =>
  flow(
    flow.inputExtends<RunPageSetupStore>(),
    If.then(
      store => !isCurrentPageSetupRunID(storeApi)(store),
      () => {
        throw new Error(
          'Canceling page setup: this run is no longer the current page setup.',
        )
      },
    ),
  )

const registerQueries = <T extends RunPageSetupStore>(storeApi: StoreApi<T>) =>
  flow(flow.inputExtends<T>(), (store: T): T => {
    const viewConfig = store.router.routeConfig?.viewConfig
    if (!viewConfig) return store
    const viewQueryConfigs = viewConfig.queries
    if (!viewQueryConfigs.length) return store

    let current: T = store
    for (const queryConfig of viewQueryConfigs) {
      try {
        current = QuerySliceActions.registerQuery(
          queryConfig,
          storeApi as StoreApi<QuerySlice>,
        )(current) as T
      } catch (error) {
        // A query can fail to register when its variable/data types aren't
        // available yet (e.g. db-subgraphs haven't finished publishing the
        // types into the API). Skip the query rather than aborting the whole
        // page setup, so the page (and PageLayout header) still renders. The
        // dependent widget will surface its own loading/error state.
        console.error(
          `Failed to register query "${queryConfig.id}" during page setup; skipping so the page can still render.`,
          error,
        )
      }
    }
    return current
  })

const viewConfigToRouteConfig = (viewConfig: ViewConfig): RouteConfig => ({
  path: viewConfig.state.path,
  routeId: viewConfig.id,
  label: viewConfig.state.label,
  routeIconName: viewConfig.state.routeIcon as any,
  screen: 'refactor out',
  roles: viewConfig.state.roles as any,
  viewConfig: viewConfig,
  element: <ScreenPage viewConfig={viewConfig} />,
})

export const RouterSliceParts = {
  synchronizeRouteStateFromBrowser,
  synchronizeRouteStateFromRouter,
  resetBrowserDocumentTitleForNavigation,
  createUniqueViewKey,
  isSameRoute,
  updateUniqueViewKey,
  setPageSetupRunId,
  isCurrentPageSetupRunID,
  throwIfStalePageSetup,
  registerQueries,
  setComponentStatus,
  synchronizeRouteConfigFromRoutes,
  viewConfigToRouteConfig,
}
